// frontend/src/utils/crsValidation.js
import { BAD_VALUE } from '../constants/crsFormOptions'; // Adjust path as needed

/**
 * Validates the CRS form data based on required fields and conditional logic.
 * @param {object} formData - The raw form data.
 * @param {object} conditionals - The calculated conditional flags.
 * @returns {string|null} - Returns an error message string if validation fails, otherwise null.
 */
export function validateCRSForm(formData, conditionals) {
  const {
    showSpouseIsPRQuestion,
    showSpouseAccompanyQuestion,
    showCanadianEducationLevel,
    showJobOfferNoc,
    showSpouseFactors,
    // showSecondLanguageScores, // Validation for scores is handled below
    // showSpouseLanguageScores // Validation for scores is handled below
  } = conditionals;

  // --- Basic Required Fields ---
  const requiredFields = [
    'maritalStatus', 'age', 'educationLevel', 'studiedInCanada',
    'firstLanguageTestValid', 'canadianWorkExperience', 'foreignWorkExperience',
    'hasCertificateOfQualification', 'hasValidJobOffer', 'hasProvincialNomination',
    'hasSiblingInCanada'
  ];
  const missingRequired = requiredFields.find(field => formData[field] === BAD_VALUE);
  if (missingRequired) {
    // Provide a more user-friendly field name if possible
    const fieldNameMap = {
        maritalStatus: 'Marital Status', age: 'Age', educationLevel: 'Level of Education',
        studiedInCanada: 'Studied in Canada', firstLanguageTestValid: 'Language Test Validity',
        canadianWorkExperience: 'Canadian Work Experience', foreignWorkExperience: 'Foreign Work Experience',
        hasCertificateOfQualification: 'Certificate of Qualification', hasValidJobOffer: 'Valid Job Offer',
        hasProvincialNomination: 'Provincial Nomination', hasSiblingInCanada: 'Sibling in Canada'
    };
    return `Please fill in the required field: ${fieldNameMap[missingRequired] || missingRequired}`;
  }

  // --- Conditional Fields ---
  if (showSpouseIsPRQuestion && formData.spouseOrPartnerIsPR === BAD_VALUE) return "Please answer if your spouse/partner is a PR or citizen.";
  if (showSpouseAccompanyQuestion && formData.spouseOrPartnerWillAccompany === BAD_VALUE) return "Please answer if your spouse/partner will accompany you.";
  if (showCanadianEducationLevel && formData.canadianEducationLevel === BAD_VALUE) return "Please select your Canadian education level.";
  if (showJobOfferNoc && formData.jobOfferNocTeer === BAD_VALUE) return "Please select the NOC TEER for the job offer.";

  // --- Language Test Validity and Scores ---
  if (formData.firstLanguageTestValid === 'A') { // 'A' means No (invalid)
    return "Language test results must be less than two years old to calculate CRS score.";
  }
  if (formData.firstLanguageTestValid === 'B') { // 'B' means Yes (valid)
    if (formData.firstLanguageTest === BAD_VALUE) return "Please select your first language test.";
    if (Object.values(formData.firstLanguageScores).some(score => score === BAD_VALUE)) return "Please enter all scores for your first language test.";

    // Check second language test selection (must select something, even N/A)
    if (formData.secondLanguageTest === BAD_VALUE) return "Please select your second language test (or 'Not applicable').";
    // Check second language scores if a test other than N/A is selected
    if (formData.secondLanguageTest !== 'NA' && Object.values(formData.secondLanguageScores).some(score => score === BAD_VALUE)) return "Please enter all scores for your second language test.";
  }

  // --- Spouse Factors (Conditional) ---
  if (showSpouseFactors) {
    if (formData.spouseEducationLevel === BAD_VALUE) return "Please select your spouse's education level.";
    if (formData.spouseCanadianWorkExperience === BAD_VALUE) return "Please select your spouse's Canadian work experience.";
    if (formData.spouseLanguageTest === BAD_VALUE) return "Please select your spouse's language test (or 'Not applicable').";
    // Check spouse language scores if a test other than N/A ('E') is selected
    if (formData.spouseLanguageTest !== 'E' && Object.values(formData.spouseLanguageScores).some(score => score === BAD_VALUE)) return "Please enter all scores for your spouse's language test.";
  }

  // If all checks pass
  return null;
}